/**
 * Client Success → Firms (ADR-0077, Phase 1).
 *
 * The portal's `Admin · Firms` roster, ported read-only into CC: a table of
 * Firm · Routing, with a client-name search and an **Originating / Handling
 * filter toggle** (a "Both"-designated firm appears under both filters — each
 * filter means "designation includes this role"). A row opens the firm info
 * panel (a record card — NOT impersonation, which stays in the portal).
 *
 * No Designation column: the filter toggle already says which designation
 * you're looking at, so a column repeating it is redundant.
 *
 * Routing is a Handling-only concern. Routing means "which handling firm
 * receives a transfer," so it is meaningless for an originating firm — the
 * Routing column and the Pause/Unpause action render ONLY in the Handling
 * view. A "Both" firm's routing is real but is managed from the Handling view,
 * where it belongs; the Originating view shows no routing at all.
 *
 * Read-only. The portal's write actions (+ Firm, per-row Pause/Edit) render
 * but are inert — Phase 1 shows them to learn which are wanted before wiring.
 *
 * Scale is small (tens of firms) so this is a flat client-side filter + search
 * with no pagination — the same posture the portal takes today.
 *
 * Spec: engineering/command-center/docs/client-success/phase-1-spec.md (evicted 2026-08-29, #775 round 10)
 */

import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import type { ClientSuccessFirm } from "@command-center/shared";
import { ClientSuccessNav } from "@/components/client-success-nav";
import { FirmDetailPanel } from "@/components/firm-detail-panel";
import { SearchBar, matchesQuery } from "@/components/search-bar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { routingVariant } from "@/lib/format";
import {
  useClientSuccessFirms,
  useFirmPause,
  useFirmUnpause,
  useFirmDocumentUpload,
  useFirmDocumentDelete,
  useFirmAddressUpdate,
  useFirmNameUpdate,
  useFirmDesignationUpdate,
  useFirmNotificationsEmailUpdate,
  useFirmEmailDomainsUpdate,
  useFirmRoutingPreferencesUpdate,
} from "@/lib/use-client-success-firms";
import { useFirmExemption } from "@/lib/use-intake-gaps";
import type { FirmDesignation, FirmDocumentSlot } from "@command-center/shared";
import { ClientError } from "@/lib/api";
import { usePanelSelection } from "@/lib/use-panel-selection";
import { useMe } from "@/lib/use-me";
import { CreateTaskModal } from "@/routes/dashboard/create-task-modal";

/** The roster's designation filter. "Both" firms match BOTH. */
type DesignationFilter = "handling" | "originating";
const FILTER_LABELS: Record<DesignationFilter, string> = {
  handling: "Handling",
  originating: "Originating",
};
const FILTERS: readonly DesignationFilter[] = ["handling", "originating"];

/** Does a firm's designation include the filtered role? "Both" → yes for both. */
function matchesDesignation(
  designation: string | null,
  filter: DesignationFilter,
): boolean {
  if (designation === "Both") return true;
  return filter === "handling"
    ? designation === "Handling"
    : designation === "Originating";
}

export function FirmsPage() {
  const firms = useClientSuccessFirms();
  const pause = useFirmPause();
  const unpause = useFirmUnpause();
  const docUpload = useFirmDocumentUpload();
  const docDelete = useFirmDocumentDelete();
  const addressUpdate = useFirmAddressUpdate();
  const nameUpdate = useFirmNameUpdate();
  const designationUpdate = useFirmDesignationUpdate();
  const notificationsEmailUpdate = useFirmNotificationsEmailUpdate();
  const emailDomainsUpdate = useFirmEmailDomainsUpdate();
  const routingPreferencesUpdate = useFirmRoutingPreferencesUpdate();
  const exemption = useFirmExemption();
  const me = useMe();
  const navigate = useNavigate();
  // Handling is the primary routing set (the larger population), so it's the
  // default filter.
  const [filter, setFilter] = useState<DesignationFilter>("handling");
  const [query, setQuery] = useState("");
  // Detail-panel selection is URL-driven (`?firm=<id>`) so deep-links from
  // the Dashboard row Identity Link open the panel on mount, and refresh
  // survives. See `usePanelSelection` (Slice I).
  const panel = usePanelSelection("firm");
  const detailId = panel.selectedId;
  const [documentError, setDocumentError] = useState<string | null>(null);
  const [addressError, setAddressError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  // `+ Ask` (Slice I) opens the CreateTaskModal pre-bound to the open panel's
  // firm. The flag mounts the modal — `initialAbout` rides the open firm id.
  const [askOpen, setAskOpen] = useState(false);

  // The single id currently being toggled (pause OR unpause). One outstanding
  // toggle at a time per firm — preventing double-clicks during the round-trip.
  // We don't disable other firms; only the one being acted on.
  const pendingFirmId =
    pause.isPending && pause.variables
      ? pause.variables.firmId
      : unpause.isPending && unpause.variables
        ? unpause.variables.firmId
        : null;

  // Slot currently in flight for THIS firm's panel. Derived from the upload OR
  // delete mutation, scoped to the open detail firm so the spinner doesn't
  // bleed to a different firm if the user quickly closes + reopens.
  const documentPendingSlot: FirmDocumentSlot | null =
    docUpload.isPending && docUpload.variables?.firmId === detailId
      ? docUpload.variables.slot
      : docDelete.isPending && docDelete.variables?.firmId === detailId
        ? docDelete.variables.slot
        : null;

  // Slice M (ADR-0100): exemption round-trip in flight for the open panel's
  // firm — same scoping pattern as documentPendingSlot.
  const exemptionPendingArtifact =
    exemption.isPending && exemption.variables?.firmId === detailId
      ? exemption.variables.artifact
      : null;

  // #1296: Pause/Unpause first asks why; the reason rides the request and the
  // audit row, and lf-ops get_firm returns it. The dialog holds the firm being
  // toggled; nothing is sent until a reason is typed.
  const [routingTarget, setRoutingTarget] = useState<ClientSuccessFirm | null>(null);

  function toggleRouting(firm: ClientSuccessFirm): void {
    if (pendingFirmId !== null) return;
    setRoutingTarget(firm);
  }

  function submitRouting(firm: ClientSuccessFirm, reason: string): void {
    const opts = { onSuccess: () => setRoutingTarget(null) };
    if (firm.routingStatus === "Paused") {
      unpause.mutate({ firmId: firm.id, reason }, opts);
    } else {
      pause.mutate({ firmId: firm.id, reason }, opts);
    }
  }

  function uploadDocument(firmId: string, slot: FirmDocumentSlot, file: File) {
    setDocumentError(null);
    docUpload.mutate(
      { firmId, slot, file },
      {
        onError: (err) => setDocumentError(formatDocumentError(err)),
      },
    );
  }

  function deleteDocument(firmId: string, slot: FirmDocumentSlot) {
    setDocumentError(null);
    docDelete.mutate(
      { firmId, slot },
      {
        onError: (err) => setDocumentError(formatDocumentError(err)),
      },
    );
  }

  function updateAddress(firmId: string, address: string | null) {
    setAddressError(null);
    addressUpdate.mutate(
      { firmId, address },
      {
        onError: (err) => setAddressError(formatAddressError(err)),
      },
    );
  }

  function updateFirmName(firmId: string, name: string) {
    setFieldError(null);
    nameUpdate.mutate(
      { firmId, name },
      { onError: (err) => setFieldError(formatFieldError(err, "name")) },
    );
  }

  function updateDesignation(firmId: string, designation: FirmDesignation) {
    setFieldError(null);
    designationUpdate.mutate(
      { firmId, designation },
      {
        onError: (err) => setFieldError(formatFieldError(err, "designation")),
      },
    );
  }

  function updateNotificationsEmail(
    firmId: string,
    notificationsEmail: string | null,
  ) {
    setFieldError(null);
    notificationsEmailUpdate.mutate(
      { firmId, notificationsEmail },
      {
        onError: (err) =>
          setFieldError(formatFieldError(err, "notifications email")),
      },
    );
  }

  function updateEmailDomains(firmId: string, emailDomains: string | null) {
    setFieldError(null);
    emailDomainsUpdate.mutate(
      { firmId, emailDomains },
      {
        onError: (err) =>
          setFieldError(formatFieldError(err, "email domains")),
      },
    );
  }

  function updateRoutingPreferences(
    firmId: string,
    routingPreferences: string | null,
  ) {
    setFieldError(null);
    routingPreferencesUpdate.mutate(
      { firmId, routingPreferences },
      {
        onError: (err) =>
          setFieldError(formatFieldError(err, "routing preferences")),
      },
    );
  }

  const addressPendingForDetail =
    addressUpdate.isPending && addressUpdate.variables?.firmId === detailId;
  const namePendingForDetail =
    nameUpdate.isPending && nameUpdate.variables?.firmId === detailId;
  const designationPendingForDetail =
    designationUpdate.isPending &&
    designationUpdate.variables?.firmId === detailId;
  const notificationsEmailPendingForDetail =
    notificationsEmailUpdate.isPending &&
    notificationsEmailUpdate.variables?.firmId === detailId;
  const emailDomainsPendingForDetail =
    emailDomainsUpdate.isPending &&
    emailDomainsUpdate.variables?.firmId === detailId;
  const routingPreferencesPendingForDetail =
    routingPreferencesUpdate.isPending &&
    routingPreferencesUpdate.variables?.firmId === detailId;

  if (firms.isLoading) {
    return (
      <Shell>
        <div className="p-8 text-sm text-plane-muted">Loading…</div>
      </Shell>
    );
  }

  if (firms.isError) {
    return (
      <Shell>
        <div className="p-8 text-sm text-error" data-testid="firms-error">
          Couldn't load firms. It will retry automatically.
        </div>
      </Shell>
    );
  }

  const all = firms.data?.firms ?? [];
  // Counts per filter (the glance) — totals, not affected by the name search.
  const filterCount: Record<DesignationFilter, number> = {
    handling: all.filter((f) => matchesDesignation(f.designation, "handling")).length,
    originating: all.filter((f) => matchesDesignation(f.designation, "originating"))
      .length,
  };
  const searching = query.trim() !== "";
  // Re-derived once so both the panel mount and the stale-link banner read the
  // same source of truth: the firm pointed at by `?firm=<id>` or null if the id
  // doesn't resolve (deleted upstream, renamed id, hand-edited URL).
  const detail = detailId ? (all.find((f) => f.id === detailId) ?? null) : null;
  const staleFirmLink = detailId !== null && detail === null;
  // Routing applies only to handling firms, so the Routing column + the
  // Pause/Unpause action show only in the Handling view.
  const showRouting = filter === "handling";
  const visible = all
    .filter((f) => matchesDesignation(f.designation, filter))
    .filter((f) => matchesQuery(f.name, query));

  return (
    <Shell>
      <div className="mb-3 flex items-center justify-between">
        <h1 className="text-lg font-sans font-semibold">
          Firms
          <span className="ml-2 text-sm font-normal text-plane-muted" data-testid="firms-count">
            {all.length}
          </span>
        </h1>
        <InertButton label="+ Firm" testId="firms-add" />
      </div>

      {/* Originating / Handling filter toggle. "Both" firms show under both. */}
      <div
        className="mb-4 flex flex-wrap gap-1 border-b"
        role="tablist"
        data-testid="firms-filter"
      >
        {FILTERS.map((f) => (
          <button
            key={f}
            type="button"
            role="tab"
            aria-selected={f === filter}
            onClick={() => setFilter(f)}
            className={cn(
              "-mb-px border-b-2 px-3 py-2 text-sm transition-colors",
              f === filter
                ? "border-active-edge font-medium text-plane-foreground"
                : "border-transparent text-plane-muted hover:text-plane-foreground",
            )}
            data-testid={`firms-filter-${f}`}
          >
            {FILTER_LABELS[f]}
            <span className="ml-2 tabular-nums text-plane-muted">
              {filterCount[f]}
            </span>
          </button>
        ))}
      </div>

      {staleFirmLink ? (
        <div
          className="mb-3 flex items-center justify-between rounded-md border border-warning-border bg-warning-bg px-3 py-2 text-sm text-warning"
          data-testid="firms-stale-link"
          role="status"
        >
          <span>This link doesn't point at a firm we know about anymore.</span>
          <button
            type="button"
            onClick={() => panel.closePanel()}
            className="rounded-md border border-warning-border px-2 py-1 text-xs font-medium hover:bg-warning-bg"
            data-testid="firms-stale-link-clear"
          >
            Clear
          </button>
        </div>
      ) : null}

      <SearchBar
        value={query}
        onChange={setQuery}
        placeholder="Search by firm name…"
        testId="firms-search"
      />

      {visible.length === 0 ? (
        <div
          className="flex min-h-[40vh] items-center justify-center text-center"
          data-testid="firms-empty"
        >
          <p className="text-lg text-plane-muted">
            {searching ? "No matches." : `No ${FILTER_LABELS[filter].toLowerCase()} firms.`}
          </p>
        </div>
      ) : (
        <Card className="overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="cursor-default hover:bg-transparent">
                <TableHead>Firm</TableHead>
                {showRouting ? <TableHead>Routing</TableHead> : null}
                <TableHead>
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visible.map((firm) => (
                <FirmRow
                  key={firm.id}
                  firm={firm}
                  showRouting={showRouting}
                  onOpen={() => {
                    setDocumentError(null);
                    panel.openPanel(firm.id);
                  }}
                  onTogglePause={() => toggleRouting(firm)}
                  pausePending={pendingFirmId === firm.id}
                />
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Re-derived `detail` is computed above so the stale-link banner and the
          panel mount read the same source of truth. Optimistic patches from
          upload/delete/pause flow through `all` into `detail` without an extra
          effect; if the firm disappears from the list, the banner takes over. */}
      {detail ? (
        <>
          <FirmDetailPanel
            firm={detail}
            onClose={() => {
              panel.closePanel();
              setDocumentError(null);
              setFieldError(null);
              setAskOpen(false);
            }}
            onTogglePause={() => toggleRouting(detail)}
            pausePending={pendingFirmId === detail.id}
            onUploadDocument={(slot, file) =>
              uploadDocument(detail.id, slot, file)
            }
            onDeleteDocument={(slot) => deleteDocument(detail.id, slot)}
            documentPendingSlot={documentPendingSlot}
            documentError={documentError}
            onAddressUpdate={(address) => updateAddress(detail.id, address)}
            addressPending={addressPendingForDetail}
            addressError={addressError}
            onNameUpdate={(name) => updateFirmName(detail.id, name)}
            namePending={namePendingForDetail}
            onDesignationUpdate={(designation) =>
              updateDesignation(detail.id, designation)
            }
            designationPending={designationPendingForDetail}
            onNotificationsEmailUpdate={(value) =>
              updateNotificationsEmail(detail.id, value)
            }
            notificationsEmailPending={notificationsEmailPendingForDetail}
            onEmailDomainsUpdate={(value) =>
              updateEmailDomains(detail.id, value)
            }
            emailDomainsPending={emailDomainsPendingForDetail}
            onRoutingPreferencesUpdate={(value) =>
              updateRoutingPreferences(detail.id, value)
            }
            routingPreferencesPending={routingPreferencesPendingForDetail}
            fieldError={fieldError}
            onAsk={me.data ? () => setAskOpen(true) : undefined}
            onClientEmails={() =>
              navigate({
                to: "/firm-success/firms/$id/client-emails",
                params: { id: detail.id },
              })
            }
            onExemption={(input) =>
              exemption.mutate({ firmId: detail.id, ...input })
            }
            exemptionPendingArtifact={exemptionPendingArtifact}
          />
          {askOpen && me.data ? (
            <CreateTaskModal
              // Key on the bound entity so a URL-driven panel switch while
              // the modal is open remounts the modal with fresh state —
              // otherwise submit would create against the previous entity.
              key={`firm:${detail.id}`}
              open
              onClose={() => setAskOpen(false)}
              viewerEmail={me.data.email}
              initialAbout={{ kind: "firm", id: detail.id }}
            />
          ) : null}
        </>
      ) : null}
      {routingTarget ? (
        <RoutingReasonDialog
          key={routingTarget.id}
          firm={routingTarget}
          pending={pendingFirmId === routingTarget.id}
          failed={
            (routingTarget.routingStatus === "Paused" ? unpause : pause).isError &&
            (routingTarget.routingStatus === "Paused" ? unpause : pause).variables?.firmId ===
              routingTarget.id
          }
          onClose={() => setRoutingTarget(null)}
          onSubmit={(reason) => submitRouting(routingTarget, reason)}
        />
      ) : null}
    </Shell>
  );
}

/** Map an api.ts error to a user-readable string for the panel banner. The
 *  worker's 4xx envelope is `{ error, ... }`; we surface a friendlier message
 *  for the common cases (size, MIME, designation gate). */
function formatDocumentError(err: Error): string {
  if (err instanceof ClientError) {
    const body = err.body as { error?: string } | null;
    switch (body?.error) {
      case "file_too_large":
        return "File is too large (max 100 MB).";
      case "unsupported_mime_type":
        return "Unsupported file type for this slot.";
      case "slot_not_applicable_to_firm_designation":
        return "This document slot doesn't apply to this firm.";
      case "missing_file":
        return "No file selected.";
      case "firm_not_found":
        return "This firm no longer exists.";
      default:
        return `Upload failed (${err.status}).`;
    }
  }
  return "Upload failed. Please try again.";
}

/** Map an api.ts error from a firm field-edit PATCH (Firm Name / Designation /
 *  Notifications Email / Email Domains / Routing Preferences) to a
 *  user-readable string for the panel banner. */
function formatFieldError(err: Error, fieldLabel: string): string {
  if (err instanceof ClientError) {
    const body = err.body as { error?: string } | null;
    switch (body?.error) {
      case "invalid_name_body":
      case "invalid_designation_body":
      case "invalid_notifications_email_body":
      case "invalid_email_domains_body":
      case "invalid_routing_preferences_body":
        return `Invalid ${fieldLabel}.`;
      case "firm_not_found":
        return "This firm no longer exists.";
      default:
        return `Couldn't save ${fieldLabel} (${err.status}).`;
    }
  }
  return `Couldn't save ${fieldLabel}. Please try again.`;
}

/** Map an api.ts error from the address-update PATCH to a user-readable string
 *  for the panel banner. */
function formatAddressError(err: Error): string {
  if (err instanceof ClientError) {
    const body = err.body as { error?: string } | null;
    switch (body?.error) {
      case "slot_not_applicable_to_firm_designation":
        return "Address only applies to Originating firms.";
      case "invalid_address_body":
        return "Address is too long (max 500 characters) or not a string.";
      case "firm_not_found":
        return "This firm no longer exists.";
      default:
        return `Couldn't save address (${err.status}).`;
    }
  }
  return "Couldn't save address. Please try again.";
}

/** Page chrome shared across the load / error / ready states so the sub-nav is
 *  always present. */
function Shell({ children }: { children: React.ReactNode }) {
  return (
    <section className="mx-auto max-w-4xl p-6" data-testid="firm-success-firms">
      <ClientSuccessNav active="firms" />
      {children}
    </section>
  );
}

function FirmRow({
  firm,
  showRouting,
  onOpen,
  onTogglePause,
  pausePending,
}: {
  firm: ClientSuccessFirm;
  showRouting: boolean;
  onOpen: () => void;
  onTogglePause: () => void;
  pausePending: boolean;
}) {
  const paused = firm.routingStatus === "Paused";
  return (
    <TableRow onClick={onOpen} data-testid="firms-row">
      <TableCell className="font-medium">{firm.name}</TableCell>
      {showRouting ? (
        <TableCell data-testid="firms-routing">
          {firm.routingStatus ? (
            <Badge variant={routingVariant(firm.routingStatus)}>
              {firm.routingStatus}
            </Badge>
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </TableCell>
      ) : null}
      {/* Per-row actions. Stop propagation so a stray click doesn't open the
          panel. Pause/Unpause is wired here against Firms.Routing Status
          (global). The legacy per-row Edit button was deleted in ADR-0091:
          every editable field has its own pencil affordance in the panel now,
          so a global Edit button has nothing to switch on. */}
      <TableCell className="w-0 text-right" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-end gap-1.5">
          {showRouting ? (
            <button
              type="button"
              onClick={onTogglePause}
              disabled={pausePending}
              data-testid="firms-row-pause"
              className={cn(
                "rounded-md border px-2 py-1 text-xs font-medium transition-colors",
                pausePending
                  ? "cursor-not-allowed text-muted-foreground opacity-60"
                  : "text-foreground hover:bg-accent",
              )}
            >
              {pausePending ? (paused ? "Unpausing…" : "Pausing…") : paused ? "Unpause" : "Pause"}
            </button>
          ) : null}
        </div>
      </TableCell>
    </TableRow>
  );
}

/** A rendered-but-inert action button (Phase 1). */
function InertButton({
  label,
  testId,
  small = false,
}: {
  label: string;
  testId: string;
  small?: boolean;
}) {
  return (
    <button
      type="button"
      disabled
      title="Not wired yet — actions land in a later phase"
      data-testid={testId}
      className={cn(
        "cursor-not-allowed rounded-md border font-medium text-muted-foreground opacity-60",
        small ? "px-2 py-1 text-xs" : "px-3 py-1.5 text-sm",
      )}
    >
      {label}
    </button>
  );
}

/**
 * Asks why a Firm's routing is being paused or resumed (#1296). Submit stays
 * disabled until the reason has text; the route refuses a blank one too.
 */
function RoutingReasonDialog({
  firm,
  pending,
  failed,
  onClose,
  onSubmit,
}: {
  firm: ClientSuccessFirm;
  pending: boolean;
  failed: boolean;
  onClose: () => void;
  onSubmit: (reason: string) => void;
}) {
  const [reason, setReason] = useState("");
  const resuming = firm.routingStatus === "Paused";
  const testId = "firms-routing-reason";
  const trimmed = reason.trim();
  return (
    <Dialog open onClose={onClose} labelledBy={`${testId}-title`} testId={testId}>
      <DialogHeader>
        <DialogTitle id={`${testId}-title`}>
          {resuming ? `Unpause ${firm.name}` : `Pause ${firm.name}`}
        </DialogTitle>
        <DialogDescription>
          {resuming
            ? "New leads will start routing to this firm again. Say why; the reason is kept with the change."
            : "Pausing stops new leads from routing to this firm; cases already with it continue. Say why; the reason is kept with the change."}
        </DialogDescription>
      </DialogHeader>
      <label className="flex flex-col gap-1.5">
        <span className="text-sm font-medium">Reason</span>
        <Textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          maxLength={2000}
          data-testid={`${testId}-text`}
        />
      </label>
      {failed ? (
        <p className="text-sm text-error" data-testid={`${testId}-error`}>
          Couldn't change the firm's routing. Try again.
        </p>
      ) : null}
      <DialogFooter>
        <Button variant="ghost" onClick={onClose} data-testid={`${testId}-cancel`}>
          Cancel
        </Button>
        <Button
          onClick={() => onSubmit(trimmed)}
          disabled={trimmed === "" || pending}
          data-testid={`${testId}-submit`}
        >
          {resuming ? "Unpause" : "Pause"}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
